import { MenuItem, Select, Switch, Tooltip } from '@material-ui/core';
import { Chip } from '@material-ui/core';
import moment from 'moment-timezone';
import React, { useState } from 'react';

import { convertECurrencyTypeToSymbol } from '~/src/dash/api/exchange-rates';
import { EPriorities, generatePriorityList } from '~/src/dash/components/filters/common';
import { ICustomRenderCellJSXProps } from '~/src/dash/components/tables/ListViewTable/common';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import { Div, fontSizing, Text } from '~/src/dash/publicaUI';
import { addCommasToDollars } from '~/src/dash/utils/basic';
import { publicaColors } from '~/src/dash/utils/colors';
import { useBaseStore } from '~/src/dash/views/base/contexts';

interface ITrendProps {
  arrow: string;
  color: string;
  backgroundColor: string;
}

const getTrendProps = (value?: number | null): ITrendProps => {
  if (value && value > 0) {
    return {
      arrow: '▲',
      color: publicaColors.darkGreen,
      backgroundColor: publicaColors.veryLightGreen,
    };
  }
  if (value && value < 0) {
    return {
      arrow: '▼',
      color: publicaColors.lightRed,
      backgroundColor: publicaColors.veryLightRed,
    };
  }
  return {
    arrow: '',
    color: publicaColors.gray,
    backgroundColor: publicaColors.veryLightGray,
  };
};

// ADD CUSTOM DATA CELL RENDERS HERE
// PLEASE ADD DESCRIPTIONS AND RETAIN ALPHABETICAL ORDER

// chips to show bidder lables in bidders table
export const BidderLabelRender = (props: ICustomRenderCellJSXProps) => {
  const values: string[] = props.value as string[];
  if (!values) return;

  return values.map((ele) => {
    return <Chip label={ele} style={{ marginRight: '10px', marginTop: '5px' }} />;
  });
};

// converts currency
export const CurrencyRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  const { session } = useBaseStore();
  const { isCurrencyConversionDisabled } = useListViewTableStore();
  const convertedValue = isCurrencyConversionDisabled
    ? (value as number).toFixed(2)
    : ((value as number) * session.publisher.ExchangeRate.ExchangeValueToUSD).toFixed(2);
  return (
    <Text small>
      {`${convertECurrencyTypeToSymbol(session.publisher.CurrencyDisplayed)} ${addCommasToDollars(
        convertedValue,
      )}`}
    </Text>
  );
};

export const CurrencyNonDecimalRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  const { session } = useBaseStore();
  const { isCurrencyConversionDisabled } = useListViewTableStore();
  const convertedValue = isCurrencyConversionDisabled
    ? (value as number).toFixed(2)
    : ((value as number) * session.publisher.ExchangeRate.ExchangeValueToUSD).toFixed(2);
  return (
    <Text small>
      {`${convertECurrencyTypeToSymbol(session.publisher.CurrencyDisplayed)} ${addCommasToDollars(
        convertedValue,
      )}`}
    </Text>
  );
};

// formats timestamp ex. '2023-04-15 05:10'
export const DateTimeRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  const dateTime = moment(value as string).format('YYYY-MM-DD HH:mm');
  return <Text small>{dateTime}</Text>;
};

// formats long strings to break with an ellipsis and add a tooltip to display full value
export const EllipsisWithTooltipRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  return (
    <Tooltip title={value} placement='top-start'>
      <div>
        <Text
          small
          bold
          style={{
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {value}
        </Text>
      </div>
    </Tooltip>
  );
};

// format an array of string or number to a list
export const ListRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  return (
    <Div style={{ textAlign: 'left' }}>
      {Array.isArray(value) ? (
        value.map((v: string | number) => <Text small>{v}</Text>)
      ) : (
        <Text small>{value}</Text>
      )}
    </Div>
  );
};

// format an array of string or number to an expandable list
interface ITruncatedLabels {
  list: JSX.Element | JSX.Element[];
  truncatedList: JSX.Element | JSX.Element[];
}
const TruncatedLabels = ({ list, truncatedList }: ITruncatedLabels) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <Tooltip arrow title={`Click to ${isExpanded ? 'collapse' : 'expand'}`} placement='bottom'>
      <div onClick={() => setIsExpanded(!isExpanded)} style={{ textAlign: 'left' }}>
        {isExpanded ? list : truncatedList}
      </div>
    </Tooltip>
  );
};
export const TruncatedListRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  const valueStr = Array.isArray(value) ? value.join(', ') : String(value);
  const truncatedList =
    valueStr.length > 15 ? (
      <Text small>{valueStr.substring(0, 15) + '...'}</Text>
    ) : (
      <Text small>{value}</Text>
    );
  const list = Array.isArray(value) ? (
    value.map((v: string | number) => <Text small>{v}</Text>)
  ) : (
    <Text small>{value}</Text>
  );
  return <TruncatedLabels list={list} truncatedList={truncatedList} />;
};

export const PercentTrendRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  const { arrow, color, backgroundColor } = getTrendProps(value as number | null);
  const updatedValue =
    value !== null ? `${arrow} ${Math.abs(value as number).toFixed(1)} %` : 'N/A';
  return (
    <Div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <Text
        extraSmall
        bold
        style={{
          textAlign: 'right',
          padding: '4px 8px',
          borderRadius: '6px',
          backgroundColor,
          color,
          maxWidth: 'auto',
        }}
      >
        {updatedValue}
      </Text>
    </Div>
  );
};

// IMPORTANT!!! Read Below
// in order to use this, you need to pass in a handleChangePriority in the customFunctions prop to ListViewTable
// creates priority / bidder tier dropdown
export const PrioritySelectRender = (props: ICustomRenderCellJSXProps) => {
  const { isWriteAuthorized, customFunctions } = useListViewTableStore();
  const value = EPriorities[props.value.toString()];
  const list = generatePriorityList(false);

  return (
    <Select
      value={value}
      onChange={(e: any) => customFunctions.handleChangePriority(e.target.value, props)}
      disabled={!isWriteAuthorized}
      style={{ fontSize: fontSizing.small }}
    >
      {Object.values(list).map((item) => {
        return <MenuItem value={item.ID}>{item.title}</MenuItem>;
      })}
    </Select>
  );
};

export const SuperUserOnlySwitchRender = (props: ICustomRenderCellJSXProps) => {
  const { isActionPending, isUpdating, customFunctions } = useListViewTableStore();
  const { value } = props;
  return (
    <Switch
      checked={value as boolean}
      disabled={isActionPending || isUpdating}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        customFunctions.handleChangeSuperUserOnly(e.target.checked, props)
      }
      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => e.stopPropagation()}
      color='secondary'
      size='small'
    />
  );
};
